import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import { Table } from "react-bootstrap";
import { FaBolt, FaPiggyBank, FaArrowUp, FaMinus, FaPlus } from "react-icons/fa";
import Loading from "react-fullscreen-loading";
import AdminDashboardLayout from "../../../Components/AdminDashboard/AdminDashboardLayout";
import ShowTime from "../../../utils/ShowTime/ShowTime";
import { useAuth } from "../../../authentication/AuthContext";
import { SLA_LABEL } from "../../../config/sla";
import {
  CREDIT_PACKS,
  BADGE_LABELS,
  DEFAULT_PACK,
  PAY_LATER_DAYS,
  MAX_PACK_QUANTITY,
  DAYS_PER_YEAR,
  ONE_YEAR_LIST_PRICE,
  packById,
  packPrice,
  packMargin,
  marginPercent,
  yearLicenses,
  costPerYearLicense,
  profitPerYearLicense,
  allowsPayLater,
  nextPackUp,
  bestMarginPack,
  money,
  rate,
  credits as fmtCredits,
  formatPercent,
} from "../../../Components/CreditPacks/creditPacks";
import "./BuyCredits.css";

// RequestQuote.message is TINYTEXT; the server trims to the same length.
const MESSAGE_MAX = 250;

const headers = () => ({ "access-token": localStorage.getItem("accessToken") });

const licensesWord = (n) => `${n} one-year license${n === 1 ? "" : "s"}`;

// Where a credit request stands, from the reseller's side. Credits landing and
// the invoice being settled are separate events — a pay-later request has the
// credits long before it is paid — so both are read, not just `status`.
function requestState(r) {
  if (r.status === "cancelled") return { tone: "cancelled", label: "Cancelled" };
  if (r.status === "completed") return { tone: "completed", label: "Completed" };
  if (r.credits_assigned_at) {
    if (r.payment_terms === "pay_later") {
      const due = new Date(new Date(r.credits_assigned_at).getTime() + PAY_LATER_DAYS * 86400000);
      return { tone: "assigned", label: `Credits added · pay by ${due.toLocaleDateString()}` };
    }
    return { tone: "assigned", label: "Credits added" };
  }
  if (r.payment_link) return { tone: "link", label: "Invoice ready" };
  return {
    tone: "pending",
    label: r.payment_terms === "pay_later" ? "Awaiting approval" : "Preparing invoice",
  };
}

export default function BuyCredits() {
  const { can } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [available, setAvailable] = useState(null);
  const [requests, setRequests] = useState([]);

  const [packId, setPackId] = useState(DEFAULT_PACK.id);
  const [quantity, setQuantity] = useState(1);
  const [terms, setTerms] = useState("prepaid");
  const [message, setMessage] = useState("");

  const pack = packById(packId);
  const payLaterAllowed = allowsPayLater(pack);
  const payLater = payLaterAllowed && terms === "pay_later";
  const total = packPrice(pack) * quantity;
  const totalCredits = pack.days * quantity;
  const totalProfit = packMargin(pack) * quantity;
  const up = nextPackUp(pack);
  const maxProfitPerLicense = profitPerYearLicense(bestMarginPack());

  const fetchRequests = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/reseller/credit_requests`, { headers: headers() });
      if (res.data.status) {
        setRequests(res.data.requests || []);
        setAvailable(res.data.available_credits ?? null);
      }
    } catch {
      // Non-fatal: the purchase form works without the history.
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (can("reseller.credits.requests")) fetchRequests();
    else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const choosePack = (id) => {
    setPackId(id);
    // Pay later belongs to the 1-year pack; moving off it falls back to prepaid
    // rather than leaving a choice selected that the server would refuse.
    if (!allowsPayLater(packById(id))) setTerms("prepaid");
  };

  const chooseWay = (way) => {
    if (way === "on_the_go") {
      setPackId("1y");
      setTerms("pay_later");
      setQuantity(1);
    } else {
      setPackId(DEFAULT_PACK.id);
      setTerms("prepaid");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/reseller/credit_requests`,
        { pack_id: pack.id, quantity, payment_terms: payLater ? "pay_later" : "prepaid", message },
        { headers: headers() }
      );
      if (res.data.status) {
        toast.success(
          payLater
            ? "Request sent — your credits land as soon as we approve it."
            : "Request sent — your invoice is on its way."
        );
        setMessage("");
        fetchRequests();
      } else {
        toast.error(res.data.message || "Could not send the request.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const onTheGo = packId === "1y" && terms === "pay_later";

  return (
    <div>
      <AdminDashboardLayout />
      <Loading loading={loading} background="#fff" loaderColor="#883484" />
      <div className="content-page-bg">
        <div className="content-page">
          <div className="content">

            {/* ── Hero ── */}
            <div className="bc-hero">
              <div>
                <h2>Buy credits. Keep more of every sale.</h2>
                <p>
                  1 credit = 1 day of license. The more you stock, the less each license costs you —
                  resell at {money(ONE_YEAR_LIST_PRICE)} a year and keep up to{" "}
                  <strong>{money(maxProfitPerLicense)}</strong> on every one.
                </p>
              </div>
              {available !== null && (
                <div className="bc-balance">
                  <div className="bc-balance__label">Available credits</div>
                  <div className="bc-balance__value">{fmtCredits(available)}</div>
                  <div className="bc-balance__note">
                    ≈ {licensesWord(Math.floor(available / DAYS_PER_YEAR))}
                  </div>
                </div>
              )}
            </div>

            {/* ── Two ways to buy ── */}
            <div className="bc-ways">
              <button type="button" className={`bc-way${onTheGo ? " is-selected" : ""}`} aria-pressed={onTheGo} onClick={() => chooseWay("on_the_go")}>
                <span className="bc-way__icon"><FaBolt /></span>
                <span>
                  <div className="bc-way__title">Buy on the go</div>
                  <div className="bc-way__desc">
                    Just onboarded a customer? Get 1-year credits now, assign the license as soon as
                    we approve, and pay within {PAY_LATER_DAYS} days.
                  </div>
                </span>
              </button>
              <button type="button" className={`bc-way${!onTheGo ? " is-selected" : ""}`} aria-pressed={!onTheGo} onClick={() => chooseWay("stock_up")}>
                <span className="bc-way__icon bc-way__icon--green"><FaPiggyBank /></span>
                <span>
                  <div className="bc-way__title">Stock up &amp; earn more</div>
                  <div className="bc-way__desc">
                    Prepay a bigger pack for a lower rate per credit — up to{" "}
                    {formatPercent(1 - bestMarginPack().ratePerCredit / CREDIT_PACKS[0].ratePerCredit)} cheaper,
                    so every license you sell earns you more.
                  </div>
                </span>
              </button>
            </div>

            {/* ── Packs ── */}
            <div className="bc-section-title">Choose a credit pack</div>
            <div className="bc-packs">
              {CREDIT_PACKS.map((p) => {
                const selected = p.id === packId;
                return (
                  <button
                    type="button"
                    key={p.id}
                    className={`bc-pack${selected ? " is-selected" : ""}`}
                    aria-pressed={selected}
                    onClick={() => choosePack(p.id)}
                  >
                    {p.badge && (
                      <span className={`bc-pack__badge bc-pack__badge--${p.badge}`}>{BADGE_LABELS[p.badge]}</span>
                    )}
                    <div className="bc-pack__name">{p.label}</div>
                    <div className="bc-pack__credits">
                      {fmtCredits(p.days)} credits · {licensesWord(yearLicenses(p))}
                    </div>
                    <div className="bc-pack__price">{money(packPrice(p))}</div>
                    <div className="bc-pack__rate">{rate(p.ratePerCredit)} per credit</div>
                    <div className="bc-pack__divider" />
                    <div className="bc-pack__row">
                      <span>Cost / license</span>
                      <strong>{money(costPerYearLicense(p))}</strong>
                    </div>
                    <div className="bc-pack__row">
                      <span>You keep</span>
                      <strong className="bc-pack__keep">{money(profitPerYearLicense(p))}</strong>
                    </div>
                    <div className="bc-pack__profit">
                      {money(packMargin(p))} profit · {formatPercent(marginPercent(p))}
                    </div>
                    {allowsPayLater(p) && <span className="bc-pack__tag">Pay later available</span>}
                  </button>
                );
              })}
            </div>

            <div className="row">
              {/* ── Order ── */}
              <div className="col-lg-7">
                <form className="bc-panel" onSubmit={submit}>
                  <h5>Your order</h5>
                  <p className="bc-muted mb-3">Prices are fixed — the request is just how we invoice you.</p>

                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div>
                      <div className="bc-section-title mb-0">Packs</div>
                      <div className="bc-muted" style={{ fontSize: "0.8rem" }}>
                        {pack.label} · {fmtCredits(pack.days)} credits each
                      </div>
                    </div>
                    <div className="bc-stepper">
                      <button type="button" onClick={() => setQuantity((q) => Math.max(1, q - 1))} disabled={quantity <= 1} aria-label="Fewer packs">
                        <FaMinus size={11} />
                      </button>
                      <span>{quantity}</span>
                      <button type="button" onClick={() => setQuantity((q) => Math.min(MAX_PACK_QUANTITY, q + 1))} disabled={quantity >= MAX_PACK_QUANTITY} aria-label="More packs">
                        <FaPlus size={11} />
                      </button>
                    </div>
                  </div>

                  <div className="bc-section-title">Payment</div>
                  {payLaterAllowed ? (
                    <div className="bc-terms">
                      <button type="button" className={`bc-term${!payLater ? " is-selected" : ""}`} aria-pressed={!payLater} onClick={() => setTerms("prepaid")}>
                        <div className="bc-term__title">Pay now</div>
                        <div className="bc-term__desc">Invoice in {SLA_LABEL}; credits land once it's paid.</div>
                      </button>
                      <button type="button" className={`bc-term${payLater ? " is-selected" : ""}`} aria-pressed={payLater} onClick={() => setTerms("pay_later")}>
                        <div className="bc-term__title">Pay within {PAY_LATER_DAYS} days</div>
                        <div className="bc-term__desc">Credits land as soon as we approve — assign the license right away.</div>
                      </button>
                    </div>
                  ) : (
                    <p className="bc-muted mb-3" style={{ fontSize: "0.85rem" }}>
                      Prepaid — that's what earns the lower rate. Need credits before you can pay?
                      Pay later is available on the 1 Year pack.
                    </p>
                  )}

                  <div className="bc-section-title">Note for our team (optional)</div>
                  <textarea
                    className="bc-textarea"
                    rows={2}
                    maxLength={MESSAGE_MAX}
                    placeholder="e.g. which customer this is for, or invoicing details"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                  />

                  <div className="bc-summary">
                    <div className="bc-summary__row">
                      <div>
                        <div className="bc-summary__title">
                          {quantity > 1 ? `${quantity} × ` : ""}{pack.label} · {fmtCredits(totalCredits)} credits
                        </div>
                        <div className="bc-summary__keep">
                          Resale value {money(pack.listPriceUsd * quantity)} · you keep {money(totalProfit)}
                        </div>
                      </div>
                      <div className="bc-summary__amount">{money(total)}</div>
                    </div>
                  </div>

                  {up && (
                    <div className="bc-upsell">
                      <FaArrowUp style={{ marginTop: 3, flexShrink: 0 }} />
                      <div>
                        Go <strong>{up.label}</strong> for {money(packPrice(up) - packPrice(pack))} more and keep{" "}
                        <strong>{money(packMargin(up) - packMargin(pack))}</strong> more profit — every license
                        costs you {money(costPerYearLicense(pack) - costPerYearLicense(up))} less.{" "}
                        <button type="button" onClick={() => choosePack(up.id)}>Switch to {up.label}</button>
                      </div>
                    </div>
                  )}

                  <button className="bc-submit" type="submit" disabled={submitting}>
                    {submitting
                      ? "Sending…"
                      : payLater
                        ? `Get ${fmtCredits(totalCredits)} credits — pay ${money(total)} within ${PAY_LATER_DAYS} days`
                        : `Request my invoice — ${money(total)}`}
                  </button>
                  <p className="bc-trust">
                    {payLater
                      ? "Nothing charged now · credits land once approved · invoice due in 30 days"
                      : `Nothing charged now · no call required · invoice in ${SLA_LABEL}`}
                  </p>
                </form>
              </div>

              {/* ── Profit ladder ── */}
              <div className="col-lg-5">
                <div className="bc-panel">
                  <h5>Your profit on every license</h5>
                  <p className="bc-muted mb-3">
                    What you keep reselling a 1-year license at {money(ONE_YEAR_LIST_PRICE)}, by the pack it came from.
                  </p>
                  {CREDIT_PACKS.map((p) => (
                    <div key={p.id} className={`bc-ladder__row${p.id === packId ? " is-selected" : ""}`}>
                      <span className="bc-ladder__label">{p.label}</span>
                      <span className="bc-ladder__track">
                        <span
                          className="bc-ladder__bar"
                          style={{ width: `${(profitPerYearLicense(p) / maxProfitPerLicense) * 100}%`, display: "block" }}
                        />
                      </span>
                      <span className="bc-ladder__value">{money(profitPerYearLicense(p))}</span>
                    </div>
                  ))}
                  <p className="bc-muted mt-3 mb-0" style={{ fontSize: "0.85rem" }}>
                    Credits work for any of your customers, any CAD platform and any term — create a
                    1-month license or a 3-year one from the same balance.
                  </p>
                </div>
              </div>
            </div>

            {/* ── History ── */}
            {can("reseller.credits.requests") && (
              <div className="bc-panel">
                <h5>Your credit requests</h5>
                {requests.length === 0 ? (
                  <p className="bc-muted mb-0">No requests yet. Your first one will show up here.</p>
                ) : (
                  <div className="table-responsive">
                    <Table hover size="sm" className="mb-0">
                      <thead>
                        <tr>
                          <th>Reference</th>
                          <th>Pack</th>
                          <th>Credits</th>
                          <th>Status</th>
                          <th>Requested</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {requests.map((r) => {
                          const state = requestState(r);
                          const showPay = r.payment_link && !["completed", "cancelled"].includes(r.status);
                          return (
                            <tr key={r.id}>
                              <td style={{ fontFamily: "monospace", fontSize: "13px" }}>{r.reference}</td>
                              <td style={{ fontSize: "13px" }}>{r.plan}</td>
                              <td style={{ fontWeight: 600 }}>{fmtCredits(r.credits)}</td>
                              <td><span className={`bc-status bc-status--${state.tone}`}>{state.label}</span></td>
                              <td style={{ fontSize: "13px", whiteSpace: "nowrap" }}>{ShowTime(r.created_at)}</td>
                              <td>
                                {showPay && (
                                  <a className="btn btn-sm admin-btn" href={r.payment_link} target="_blank" rel="noopener noreferrer">
                                    Pay now
                                  </a>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                )}
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}
